import { FunctionComponent } from "react";

import { Heading, Stack } from "@bps/fluent-ui";
import { TenantNetworkAuthenticationSettingsDto } from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddTenantNetworkAuthenticationSetting,
  useDeleteTenantNetworkAuthenticationSetting,
  useToggleTenantNetworkAuthenticationSetting,
  useUpdateTenantNetworkAuthenticationSetting
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { useFeedbackContext } from "../../../../../../libs/stores/feedback-context/feedback-context";

import {
  AddIpRangeFormValues,
  NetworkAuthenticationSettingsSection
} from "../../_shared-blocks";

interface TenantUsersIpRangesSectionProps {
  tenantId: string;
  hasWritePermission: boolean;
  networkSettings?: TenantNetworkAuthenticationSettingsDto[];
}

export const TenantUsersIpRangesSection: FunctionComponent<TenantUsersIpRangesSectionProps> = ({
  tenantId,
  hasWritePermission,
  networkSettings
}) => {
  const { error } = useFeedbackContext();

  const {
    mutateAsync: addNetworkSetting
  } = useAddTenantNetworkAuthenticationSetting();

  const {
    mutateAsync: updateNetworkSetting
  } = useUpdateTenantNetworkAuthenticationSetting();

  const {
    mutateAsync: deleteNetworkSetting
  } = useDeleteTenantNetworkAuthenticationSetting(tenantId);

  const {
    mutateAsync: toggleNetworkSetting
  } = useToggleTenantNetworkAuthenticationSetting();

  const handleAddIpRange = async (
    values: AddIpRangeFormValues,
    id?: string
  ) => {
    const payload = {
      tenantId,
      endAddress: values.endAddress,
      startAddress: values.startAddress,
      description: values.description,
      isActive: true
    };

    if (networkSettings && id) {
      await updateNetworkSetting({ id, payload });
    } else {
      await addNetworkSetting(payload);
    }
  };

  const handleRemoveIpRange = async (id: string) => {
    try {
      await deleteNetworkSetting(id);
    } catch (e) {
      error(e);
    }
  };

  const handleToggleIpRange = async (id: string, shouldDisable: boolean) => {
    try {
      await toggleNetworkSetting({ id, disable: shouldDisable });
    } catch (e) {
      error(e);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Heading variant="section-heading">Allow Listed IP Addresses</Heading>
      <NetworkAuthenticationSettingsSection
        tenantId={tenantId}
        hasWritePermission={hasWritePermission}
        networkSettings={networkSettings}
        handleAddIpRange={handleAddIpRange}
        handleRemoveIpRange={handleRemoveIpRange}
        handleToggleIpRange={handleToggleIpRange}
      />
    </Stack>
  );
};
